// pages/ViewStaff.js - View Staff Details (Light Theme)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, User, Mail, Phone,
    Building, Calendar,
    Edit, Trash2, IdCard, MapPin, Loader2, AlertCircle,
    Lock, Eye, EyeOff, KeyRound, CheckCircle2
} from 'lucide-react';
import { toast } from 'react-toastify';

const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl p-4 sm:p-5 transition-all bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}
    >
        {children}
    </div>
);

// ── API Configuration ───────────────────────────────────────────────────────
const STAFF_API_BASE_URL = 'https://api.mobilink.tech/api/superAdmin/staff';
const AUTH_TOKEN = sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken') || '';

// ── Staff Service (wired to live API) ───────────────────────────────────────
const StaffService = {
    getById: async (id) => {
        const res = await fetch(`${STAFF_API_BASE_URL}/getstaffbyid`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({ staffId: id })
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to fetch staff details');
        }
        return data.staff;
    },

    delete: async (id) => {
        const res = await fetch(`${STAFF_API_BASE_URL}/deletestaff/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${AUTH_TOKEN}`
            }
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
            throw new Error(data.message || 'Failed to delete staff');
        }
        return data;
    },

    changePassword: async (id, newPassword, confirmPassword) => {
        const res = await fetch(`${STAFF_API_BASE_URL}/changepassword/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({ newPassword, confirmPassword })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
            throw new Error(data.message || 'Failed to change password');
        }
        return data;
    }
};

const getInitials = (name = '') =>
    name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';

const formatDate = (isoString) => {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── Change Password Section ─────────────────────────────────────────────────
const ChangePasswordSection = ({ staffId }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState('');

    const validate = () => {
        const newErrors = {};
        if (!newPassword) newErrors.newPassword = 'New password is required';
        else if (newPassword.length < 6) newErrors.newPassword = 'Password must be at least 6 characters';
        if (!confirmPassword) newErrors.confirmPassword = 'Please confirm the new password';
        else if (newPassword && confirmPassword !== newPassword) newErrors.confirmPassword = 'Passwords do not match';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        setSuccess('');
        if (!validate()) return;

        setSaving(true);
        try {
            await StaffService.changePassword(staffId, newPassword, confirmPassword);
            setSuccess('Password updated successfully.');
            toast.success('Password updated successfully.');
            setNewPassword('');
            setConfirmPassword('');
            setErrors({});
        } catch (err) {
            setApiError(err.message || 'Failed to change password');
            toast.error(err.message || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Panel>
            <div className="flex items-center gap-2 mb-4">
                <KeyRound size={18} className="text-orange-500" />
                <h3 className="text-sm font-semibold text-gray-800">Change Password</h3>
            </div>

            {apiError && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{apiError}</span>
                </div>
            )}

            {success && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-green-50 border border-green-200 text-green-600 text-sm">
                    <CheckCircle2 size={16} />
                    <span>{success}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-600">New Password *</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                placeholder="Enter new password"
                            />
                            <button type="button" onClick={() => setShowNew(!showNew)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500">
                                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        {errors.newPassword && <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-600">Confirm Password *</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                placeholder="Confirm new password"
                            />
                            <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500">
                                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>}
                    </div>
                </div>

                <div className="flex justify-end">
                    <button type="submit" disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all disabled:opacity-70 bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm">
                        {saving
                            ? <><Loader2 size={16} className="animate-spin" /> Updating...</>
                            : <><KeyRound size={16} /> Update Password</>}
                    </button>
                </div>
            </form>
        </Panel>
    );
};

export const SingleStaff = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [staff, setStaff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const data = await StaffService.getById(id);
                if (!cancelled) setStaff(data);
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || 'Failed to load staff details');
                    toast.error(err.message || 'Failed to load staff details');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    const handleEdit = () => navigate(`/admin/staff/add/${id}`);
    const handleBack = () => navigate('/admin/staff');

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this staff member?')) return;
        setDeleting(true);
        setDeleteError('');
        try {
            await StaffService.delete(id);
            toast.success('Staff member deleted successfully!');
            navigate('/admin/staff');
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete staff');
            toast.error(err.message || 'Failed to delete staff');
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-2xl">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-400 border-t-transparent mx-auto" />
                    <p className="mt-4 text-sm text-gray-500">Loading...</p>
                </div>
            </div>
        );
    }

    if (error || !staff) {
        return (
            <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-2xl">
                <div className="text-center">
                    <User size={48} className="mx-auto mb-3 opacity-30 text-gray-400" />
                    <h2 className="text-xl font-bold text-gray-800">Staff Not Found</h2>
                    {error && (
                        <p className="mt-2 text-sm text-red-500 flex items-center justify-center gap-1.5">
                            <AlertCircle size={14} /> {error}
                        </p>
                    )}
                    <button onClick={handleBack}
                        className="mt-4 px-4 py-2 rounded-xl transition-all text-sm bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                        Back to Staff
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <button onClick={handleBack}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <h1 className="text-lg sm:text-xl font-bold text-gray-800">Staff Details</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleEdit}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                        <Edit size={16} /> Edit
                    </button>
                    <button onClick={handleDelete} disabled={deleting}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 disabled:opacity-50">
                        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>

            {deleteError && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{deleteError}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Profile Card */}
                <Panel className="lg:col-span-1">
                    <div className="text-center">
                        {staff.image ? (
                            <img src={staff.image} alt={staff.name}
                                className="w-24 h-24 rounded-2xl object-cover mx-auto shadow-md" />
                        ) : (
                            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-2xl font-bold mx-auto shadow-md">
                                {getInitials(staff.name)}
                            </div>
                        )}
                        <h2 className="text-xl font-bold mt-3 text-gray-800">{staff.name}</h2>
                        <p className="text-sm text-gray-500">{staff.roleName || '—'}</p>
                        <div className="mt-3 flex justify-center">
                            <span className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${staff.status === 'active' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${staff.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                                {staff.status}
                            </span>
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-orange-200">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 text-sm">
                                <IdCard size={16} className="text-gray-400" />
                                <span className="text-gray-700 break-all">Id: {staff._id}</span>
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Details Card */}
                <Panel className="lg:col-span-2">
                    <h3 className="text-sm font-semibold mb-4 text-gray-800">Staff Information</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Full Name</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.name}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.email}</p>
                        </div>
                        <div onClick={()=>navigate(`/admin/roles/details/${staff.roleId}`)} className="p-3 rounded-xl bg-orange-50 border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors">
                            <p className="text-xs text-gray-500">Role</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.roleName || '—'}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Department</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.departmentName || '—'}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.phone}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Address</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{staff.address || '—'}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Joined</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{formatDate(staff.createdAt)}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-500">Last Updated</p>
                            <p className="text-sm font-semibold mt-0.5 text-gray-800">{formatDate(staff.updatedAt)}</p>
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Change Password */}
            <ChangePasswordSection staffId={staff._id} />
        </div>
    );
};

export default SingleStaff;